import { FetchResponse } from '@zapier/identity-fetch';
import camelCase from 'lodash/camelCase';

import { SignupErrors, SignupResult, SignupResultType } from './types';
import { GENERIC_ERROR_MESSAGE } from './constants';
import { urls } from '@zapier/identity-utils';

// Ensures we always use the same key for fields that the
// server will sometimes return with different keys
const fieldConversions: Record<string, string> = {
  captcha_required: 'captcha',
  password1: 'password',
};

export function parseSignupErrors(resp: FetchResponse<SignupResult>) {
  const parsedErrors: SignupErrors = {};
  let resultType = SignupResultType.IS_VALID_SIGNUP;
  let redirectUrl = '';

  try {
    const errors = resp.body.errors;

    // Signup succeeded but the account is gated behind email verification.
    // Redirect to the verify-email page (the hook appends the email param).
    if (resp.body?.requires_email_verification) {
      return {
        errors: parsedErrors,
        redirectUrl: urls.VERIFY_EMAIL_URL,
        type: SignupResultType.IS_EMAIL_VERIFICATION_REQUIRED,
      };
    }

    if (!resp.headers?.get('content-type')?.includes('application/json')) {
      // The server responded with HTML which normally means cookies have
      // been disabled
      parsedErrors.error = 'It looks like you have cookies disabled';
      resultType = SignupResultType.IS_MISSING_CSRF;
      redirectUrl = urls.COOKIE_ERROR_URL;
    }

    if (resp.status === 429) {
      // The server says we are trying to often
      parsedErrors.error =
        'It looks like you are trying too often. If you’re having trouble, please contact support.';
      resultType = SignupResultType.TRYING_TOO_OFTEN;
    }

    // See if we need to parse the errors array or not
    if (Array.isArray(errors) && errors.length > 0) {
      const firstError = errors[0];

      // Some errors are returned as a string
      if (firstError && typeof firstError === 'string') {
        parsedErrors.error = firstError;
        // We don't know what type of error this is so set UNKNOWN
        if (resultType === SignupResultType.IS_VALID_SIGNUP) {
          resultType = SignupResultType.UNKNOWN_ERROR;
        }
      }

      // Contextual messages like "You are already logged in."
      // @ts-ignore
      if (firstError && typeof firstError === 'object' && firstError?.message) {
        // @ts-ignore
        parsedErrors.error = firstError?.message;
        if (resultType === SignupResultType.IS_VALID_SIGNUP) {
          resultType = SignupResultType.UNKNOWN_ERROR;
        }
      }

      // One or more fields has returned an error
      errors.forEach(error => {
        if (typeof error === 'string') {
          return;
        }

        const fields = error?.fields;

        if (fields) {
          fields.forEach(field => {
            // Coalesce inconsistent server field keys with fieldConversions
            // otherwise camelCase the keys
            const key = fieldConversions[field.key] || camelCase(field.key);
            // @ts-ignore
            parsedErrors[key] = field.message;
          });
        }
      });

      /**
       * We have an error, but we don't know how to deal with it. So throw
       * a generic error.
       */
      if (Object.keys(parsedErrors).length === 0) {
        throw resp;
      }
    }
  } catch (err) {
    /**
     * Something has blown up, maybe the API is down or the client might
     * be disconnected, or we have an error we cannot parse. In this case
     * send along a generic error.
     */
    parsedErrors.error = GENERIC_ERROR_MESSAGE;
    if (resultType === SignupResultType.IS_VALID_SIGNUP) {
      resultType = SignupResultType.UNKNOWN_ERROR;
    }
  }

  return {
    errors: parsedErrors,
    redirectUrl,
    type: resultType as SignupResultType,
  };
}
